/**
 * Trunnion AI - Command Palette Overlay
 * Full-screen Cmd+K overlay for quick navigation, search, and actions.
 * Reads commands from the CommandPaletteContext, filters them via fuzzy search,
 * and groups results by section.
 */

import { useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Search } from 'lucide-react';
import { fuzzySearch } from '@/lib/fuzzySearch';
import { CommandPaletteContext } from '@/components/command-palette/CommandPaletteProvider';
import { CommandGroup } from '@/components/command-palette/CommandGroup';
import type { Command } from '@/components/command-palette/CommandItem';

// The order in which sections are displayed
const SECTION_ORDER: Command['section'][] = ['Navigation', 'Actions', 'Recent'];

export function CommandPalette() {
  const { isOpen, close, commands } = useContext(CommandPaletteContext);

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // --- Reset state when opened / closed -----------------------------------

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      // Focus the input after a microtask so the element is mounted
      requestAnimationFrame(() => inputRef.current?.focus());
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // --- Fuzzy-filtered results ---------------------------------------------

  const filteredCommands = useMemo(() => {
    const results = fuzzySearch(commands, query, (cmd) => cmd.label);
    return results.map((r) => r.item);
  }, [commands, query]);

  // --- Group results by section -------------------------------------------

  const groups = useMemo(() => {
    const grouped: { title: string; items: Command[]; startIndex: number }[] = [];
    let runningIndex = 0;

    for (const section of SECTION_ORDER) {
      const items = filteredCommands.filter((cmd) => cmd.section === section);
      if (items.length > 0) {
        grouped.push({ title: section, items, startIndex: runningIndex });
        runningIndex += items.length;
      }
    }

    return grouped;
  }, [filteredCommands]);

  const totalCount = filteredCommands.length;

  // --- Execute a command ---------------------------------------------------

  const executeCommand = useCallback(
    (cmd: Command) => {
      cmd.action();
      close();
    },
    [close],
  );

  // --- Keyboard navigation ------------------------------------------------

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % Math.max(totalCount, 1));
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev <= 0 ? Math.max(totalCount - 1, 0) : prev - 1,
          );
          break;
        }
        case 'Enter': {
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            executeCommand(filteredCommands[selectedIndex]);
          }
          break;
        }
        case 'Escape': {
          e.preventDefault();
          close();
          break;
        }
        default:
          break;
      }
    },
    [totalCount, filteredCommands, selectedIndex, executeCommand, close],
  );

  // --- Reset selection when filtered results change -----------------------

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // --- Do not render when closed ------------------------------------------

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] p-4"
      onClick={(e) => {
        if (e.target === overlayRef.current) close();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Dialog */}
      <div
        className="relative w-full max-w-xl rounded-xl border border-trunnion-slate-700 bg-trunnion-slate-800 shadow-2xl animate-slide-up"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-trunnion-slate-700 px-4 py-3">
          <Search className="h-5 w-5 shrink-0 text-trunnion-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a command or search..."
            className="flex-1 bg-transparent text-sm text-white placeholder-trunnion-slate-400 outline-none"
            aria-label="Search commands"
            aria-activedescendant={
              filteredCommands[selectedIndex]
                ? `cmd-${filteredCommands[selectedIndex].id}`
                : undefined
            }
            role="combobox"
            aria-expanded
            aria-controls="command-palette-results"
            aria-autocomplete="list"
          />
          <kbd className="hidden sm:inline-flex shrink-0 rounded bg-trunnion-slate-700 border border-trunnion-slate-600 px-1.5 py-0.5 text-xs font-mono text-trunnion-slate-400">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div
          id="command-palette-results"
          role="listbox"
          className="max-h-[50vh] overflow-y-auto py-2"
        >
          {groups.length > 0 ? (
            groups.map((group) => (
              <CommandGroup
                key={group.title}
                title={group.title}
                items={group.items}
                selectedIndex={selectedIndex}
                startIndex={group.startIndex}
                onSelect={executeCommand}
                onHover={setSelectedIndex}
              />
            ))
          ) : (
            <div className="px-4 py-8 text-center text-sm text-trunnion-slate-400">
              No results found for{' '}
              <span className="font-medium text-trunnion-slate-300">
                &ldquo;{query}&rdquo;
              </span>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-between border-t border-trunnion-slate-700 px-4 py-2 text-xs text-trunnion-slate-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="rounded bg-trunnion-slate-700 border border-trunnion-slate-600 px-1 py-0.5 font-mono text-[10px]">
                &uarr;
              </kbd>
              <kbd className="rounded bg-trunnion-slate-700 border border-trunnion-slate-600 px-1 py-0.5 font-mono text-[10px]">
                &darr;
              </kbd>
              <span className="ml-0.5">navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded bg-trunnion-slate-700 border border-trunnion-slate-600 px-1.5 py-0.5 font-mono text-[10px]">
                &crarr;
              </kbd>
              <span className="ml-0.5">select</span>
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="rounded bg-trunnion-slate-700 border border-trunnion-slate-600 px-1.5 py-0.5 font-mono text-[10px]">
              Esc
            </kbd>
            <span className="ml-0.5">close</span>
          </span>
        </div>
      </div>
    </div>
  );
}
